import { useCallback, useEffect, useState } from 'react';
import { REGIONS, REGION_IDS, isRegionId, type RegionId } from '@adtu/shared';
import {
  type DownloadProgress,
  type OfflineFile,
  formatBytes,
  getStorageEstimate,
} from '../lib/offline';
import {
  clearAllOpfs,
  downloadToOpfs,
  opfsStatus,
  removeFromOpfs,
} from '../lib/opfsStore';
import { tilesManifest, type RegionManifestEntry } from '../lib/api';
import {
  POI_CATEGORIES,
  type PoiCategoryId,
  clearPoiCache,
  forceRefreshPoiCategory,
  getPoiCacheInfo,
} from '../lib/poi';
import { useServiceWorkerState, type ServiceWorkerState } from '../lib/sw';
import { isDynamicRegionSlug, useActiveRegion } from '../lib/useUiSettings';

interface FileState {
  file: OfflineFile;
  cached: boolean;
  cachedSize?: number;
  progress?: DownloadProgress;
  error?: string;
  abort?: AbortController;
}

/**
 * Wave 14cf: pro Region eine Sektion mit map/contours/hillshade. Jede
 * Region hat ihren eigenen Block + lokalen FileState. Die "active region"
 * (was die App beim Start anzeigt) ist davon entkoppelt — wer Liguria
 * offline geladen hat aber Elba als active gewählt hat, kann jederzeit
 * switchen ohne Online-Zwang.
 */
interface RegionSection {
  region: RegionManifestEntry;
  files: FileState[];
}

/**
 * POI-Cache-State pro Kategorie. Lebt parallel zu den Kartendateien in der
 * gleichen Offline-Page — User-Wunsch Batch 8: "ichhätte sie alle echt gern
 * in der offline section unter relief bitte und nicht getrennt als cache".
 * Ein Ort für alles, was offline verfügbar sein muss.
 */
interface PoiCacheState {
  category: PoiCategoryId;
  label: string;
  emoji: string;
  description: string;
  count: number;
  oldestCachedAt: string | null;
  /** true während eines `forceRefreshPoiCategory`-Laufs. */
  busy: boolean;
  error?: string;
}

// Beschreibung pro Kategorie — was der User davon hat und wie groß (grob)
// der Download wird. Keine exakte Größe, weil Overpass nicht pro-Request
// vorhersagt wieviele Items kommen; die Beschreibung soll dem User helfen
// zu entscheiden ob er das lokal haben will.
// Optionale, ausführlichere Beschreibung pro Kategorie. Fehlt eine, fällt
// loadAllPoiCacheStates auf den kurzen Hint aus POI_CATEGORIES zurück.
const POI_DESCRIPTIONS: Partial<Record<PoiCategoryId, string>> = {
  drinking_water:
    'OpenStreetMap fountains, taps and springs along the whole route corridor. Tiny download, huge value when you run dry on a hot day with no signal.',
};

export function OfflinePage() {
  // Wave 14cf: per-region Sections statt einer flachen Liste.
  const [sections, setSections] = useState<RegionSection[]>([]);
  const [pois, setPois] = useState<PoiCacheState[]>([]);
  const [storage, setStorage] = useState<{ usage: number; quota: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [activeRegion, setActiveRegion] = useActiveRegion();

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const [manifest, est] = await Promise.all([
        tilesManifest({ forceNetwork: true }),
        getStorageEstimate(),
      ]);
      setStorage(est);

      // ULTRA Single-Route-Setup: Quelle ist ausschließlich manifest.regions[].
      // (Der alte Top-Level-Slot-Fallback aus der MTB-App, der eine synthetische
      // Liguria-Region baute, ist entfernt — es gibt nur noch die 3-Peaks-Route.)
      const regionEntries: RegionManifestEntry[] = manifest.regions ?? [];

      const built = await Promise.all(
        regionEntries.map(async (region) => {
          const list: OfflineFile[] = [];
          if (region.map) {
            list.push({
              key: `${region.id}:map`,
              title: `${region.humanName} map`,
              description: 'Roads, woods, villages, trails (zoom 6–14)',
              url: region.map.url,
              sizeBytes: region.map.sizeBytes,
            });
          }
          if (region.contours) {
            list.push({
              key: `${region.id}:contours`,
              title: 'Contour lines',
              description: 'SRTM-based, 20-m interval',
              url: region.contours.url,
              sizeBytes: region.contours.sizeBytes,
            });
          }
          if (region.hillshade) {
            list.push({
              key: `${region.id}:hillshade`,
              title: 'Relief (3D look)',
              description: 'SRTM hillshade for 3D terrain rendering',
              url: region.hillshade.url,
              sizeBytes: region.hillshade.sizeBytes,
            });
          }
          const fileStates = await Promise.all(
            list.map(async (file) => {
              const status = await opfsStatus(file.url);
              return {
                file,
                cached: status.exists,
                cachedSize: status.size,
              };
            }),
          );
          return { region, files: fileStates } satisfies RegionSection;
        }),
      );

      // Regionen ohne verfügbare Files (Tiles noch nicht gebaut) NICHT als
      // leere Sektion zeigen — sie fallen unten in die 'coming soon'-Stub-Liste.
      setSections(built.filter((s) => s.files.length > 0));
      setPois(await loadAllPoiCacheStates());
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Helper: mutiert eine Sektion in-place (immutable copy) und wendet die
  // Mutation auf den FileState an Index `idx` an. Reduziert die
  // setSections-Boilerplate die sonst in jedem Download-Handler steht.
  function patchFile(
    sectionIdx: number,
    fileIdx: number,
    patch: Partial<FileState>,
  ) {
    setSections((prev) =>
      prev.map((sec, sIdx) =>
        sIdx === sectionIdx
          ? {
              ...sec,
              files: sec.files.map((f, fIdx) =>
                fIdx === fileIdx ? { ...f, ...patch } : f,
              ),
            }
          : sec,
      ),
    );
  }

  async function download(sectionIdx: number, fileIdx: number) {
    const section = sections[sectionIdx];
    const current = section?.files[fileIdx];
    if (!current) return;

    const abort = new AbortController();
    patchFile(sectionIdx, fileIdx, { error: undefined, abort });

    try {
      await downloadToOpfs(
        current.file.url,
        (progress) => {
          patchFile(sectionIdx, fileIdx, { progress });
        },
        abort.signal,
      );
      await refresh();
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        patchFile(sectionIdx, fileIdx, { progress: undefined, abort: undefined });
      } else {
        patchFile(sectionIdx, fileIdx, {
          progress: undefined,
          abort: undefined,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }

  function cancel(sectionIdx: number, fileIdx: number) {
    sections[sectionIdx]?.files[fileIdx]?.abort?.abort();
  }

  // Wave 14ea: window.confirm ist raus. In der iOS-Homescreen-PWA
  // (display: standalone) sind confirm/alert/prompt unzuverlaessig — der
  // Dialog erscheint nicht, confirm() liefert still `false`, und der Button
  // tut scheinbar gar nichts. Genau dieser Bug hat schon beim Loeschen von
  // Korridor-Paketen zugeschlagen. Consent laeuft jetzt ueber eine
  // Bestaetigungszeile IN der Seite (siehe `armedRegion`).
  async function downloadRegion(sectionIdx: number) {
    const section = sections[sectionIdx];
    if (!section) return;
    setArmedRegion(null);
    for (let i = 0; i < section.files.length; i++) {
      if (!section.files[i]?.cached) {
        await download(sectionIdx, i);
      }
    }
  }

  // Wave 14ec: letzte window.confirm-Reste raus. In der iOS-Homescreen-PWA
  // (display: standalone) unterdrueckt WebKit confirm() — der Aufruf liefert
  // still `false`, der Handler steigt aus, und fuer den User "passiert beim
  // Klick gar nichts". Genau dieser Report kam: "wenn ich auf Paket loeschen
  // klicke, passiert NICHTS". Bestaetigt wird jetzt ueber <DangerConfirm/>,
  // eine Zeile IN der Seite. Die Handler hier loeschen bedingungslos.
  async function removeRegion(sectionIdx: number) {
    const section = sections[sectionIdx];
    if (!section) return;
    for (const f of section.files) {
      if (f.cached) {
        try {
          await removeFromOpfs(f.file.url);
        } catch (err) {
          console.warn(`[offline] removeFromOpfs ${f.file.url}`, err);
        }
      }
    }
    await refresh();
  }

  async function remove(sectionIdx: number, fileIdx: number) {
    const section = sections[sectionIdx];
    const current = section?.files[fileIdx];
    if (!current) return;
    await removeFromOpfs(current.file.url);
    await refresh();
  }

  // "wie kann ich alle daten löschen?" — der Button hiess zwar "Delete ALL",
  // raeumte aber nur die Kartenfiles im OPFS weg und liess die POI-Caches
  // (IndexedDB) stehen. Jetzt geht beides in einem Rutsch.
  async function clearAll() {
    await clearAllOpfs();
    for (const cat of Object.keys(POI_CATEGORIES) as PoiCategoryId[]) {
      try {
        await clearPoiCache(cat);
      } catch (err) {
        console.warn(`[offline] clearPoiCache ${cat}`, err);
      }
    }
    await refresh();
  }

  // POI-Download: wir nutzen `forceRefreshPoiCategory` (löscht und ersetzt den
  // Cache atomar). Kein Progress-Bar — Overpass-Responses sind klein (<1 MB),
  // kommen in einem Rutsch. Stattdessen zeigen wir "Downloading…" inline.
  async function downloadPoi(category: PoiCategoryId) {
    setPois((prev) =>
      prev.map((p) => (p.category === category ? { ...p, busy: true, error: undefined } : p)),
    );
    try {
      const { count } = await forceRefreshPoiCategory(category);
      setPois((prev) =>
        prev.map((p) =>
          p.category === category
            ? { ...p, busy: false, count, oldestCachedAt: new Date().toISOString() }
            : p,
        ),
      );
      // Storage-Estimate aktualisieren — POIs sind klein, aber aufsummiert spürbar.
      const est = await getStorageEstimate();
      setStorage(est);
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      // Der gängigste User-Fehler: offline. Wir wollen KEIN "failed to fetch"
      // zeigen — das hilft niemandem. Stattdessen menschlicher formulieren.
      //
      // iOS-Safari-spezifisch: "database connection is closing" passiert wenn
      // iOS während des Overpass-Fetches die IDB-Connection schließt. Das
      // storage/db.ts-withDb macht schon 1 Retry automatisch — wenn's trotzdem
      // durchrutscht, ist ein Tap auf "Download" nochmal der Fix.
      // iOS-Safari IDB-Fehler kommen in mehreren Geschmacksrichtungen:
      //   - "database connection is closing" → einzelne Connection tot,
      //     withDb retried schon 2× — wenn's trotzdem hier landet, ist
      //     ein zweiter Tap meist der Fix.
      //   - "Connection to Indexed Database server lost. Refresh the page
      //     to try again." → iOS hat den IDB-Server-Prozess gekillt.
      //     Nur ein echter Page-Reload hilft hier zuverlässig.
      const isServerLost = /server lost|refresh the page/i.test(raw);
      const isClosingClass =
        /connection is closing|invalidstate|database connection|closed (db|database)|idbdatabase/i.test(
          raw,
        );
      const friendly = /failed to fetch|network|typeerror/i.test(raw)
        ? 'Download failed — looks like the app can\'t reach the internet right now.'
        : /rate-limited|429/i.test(raw)
        ? 'All OpenStreetMap mirrors are rate-limited right now. Try again in a few minutes.'
        : isServerLost
        ? 'Safari lost its offline-cache connection. Please reload the page (pull down from the top) and tap Download again.'
        : isClosingClass
        ? 'The browser briefly closed the offline cache. Tap Download once more — it usually works the second time.'
        : raw;
      setPois((prev) =>
        prev.map((p) =>
          p.category === category ? { ...p, busy: false, error: friendly } : p,
        ),
      );
    }
  }

  async function removePoi(category: PoiCategoryId) {
    setPois((prev) =>
      prev.map((p) => (p.category === category ? { ...p, busy: true, error: undefined } : p)),
    );
    try {
      await clearPoiCache(category);
      setPois((prev) =>
        prev.map((p) =>
          p.category === category
            ? { ...p, busy: false, count: 0, oldestCachedAt: null }
            : p,
        ),
      );
    } catch (err) {
      setPois((prev) =>
        prev.map((p) =>
          p.category === category
            ? { ...p, busy: false, error: err instanceof Error ? err.message : String(err) }
            : p,
        ),
      );
    }
  }

  // Welcher Region-Block hat gerade "wirklich laden?" offen (Index) — ersetzt
  // den alten window.confirm in downloadRegion.
  const [armedRegion, setArmedRegion] = useState<number | null>(null);

  const allFiles = sections.flatMap((s) => s.files);
  const anyDownloading = allFiles.some((f) => f.progress);
  const anyCached = allFiles.some((f) => f.cached);

  return (
    <div className="absolute inset-0 overflow-auto p-4 pb-8">
      <h1 className="text-xl font-semibold mb-1">Offline downloads</h1>
      <p className="text-white/60 text-sm mb-4">
        Download the map data per region. You can keep both regions offline,
        or just the one you ride in.
      </p>

      <ServiceWorkerStatusCard />

      {storage && (
        <div className="bg-turbo-700/30 rounded-lg p-3 mb-4 text-xs">
          <div className="flex justify-between text-white/70">
            <span>Device storage</span>
            <span>
              {formatBytes(storage.usage)} / {formatBytes(storage.quota)}
            </span>
          </div>
          <div className="mt-1 h-1.5 bg-turbo-900/50 rounded overflow-hidden">
            <div
              className="h-full bg-turbo-500"
              style={{ width: `${Math.min(100, (storage.usage / storage.quota) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Wave 14cf: Active-Region-Switcher. Wirkt sich auf MapView-Camera
          und Routing-DB-Auswahl aus, NICHT auf den Offline-Cache (jede
          Region hat ihren eigenen Cache-State weiter unten). */}
      {sections.length >= 2 && (
        <div className="bg-turbo-700/30 rounded-lg p-3 mb-4">
          <div className="text-xs uppercase tracking-wider text-white/50 mb-2">
            Active region
          </div>
          {/* Wave 14es: flex-wrap + min-width statt fix flex-1, damit ab 4-5
              Regionen die hinteren in eine zweite Reihe rutschen statt off-
              screen zu kommen. */}
          <div className="flex flex-wrap gap-2">
            {sections.map((sec) => {
              const isActive = sec.region.id === activeRegion;
              // Wave 14dp: vorher hardcoded auf liguria|elba — Finale Outdoor
              // war damit deaktiviert auch wenn die Daten runtergeladen
              // waren. Jetzt via isRegionId-Guard aus @adtu/shared damit
              // alle in REGION_IDS gelisteten Regionen aktivierbar sind.
              // Corridor-Selfservice: auch dynamische Korridor-Regionen
              // (Server-Manifest, Slug-Format) sind aktivierbar — Map-Camera,
              // Basemap und Routing-DB kommen dann aus dem Korridor-Paket.
              const knownId = isRegionId(sec.region.id)
                ? (sec.region.id as RegionId)
                : isDynamicRegionSlug(sec.region.id)
                  ? (sec.region.id as RegionId)
                  : null;
              return (
                <button
                  key={sec.region.id}
                  type="button"
                  disabled={!knownId}
                  onClick={() => knownId && setActiveRegion(knownId)}
                  className={`grow basis-[calc(33%-0.5rem)] min-w-[5.5rem] rounded-lg py-2 px-2 text-sm font-medium border transition ${
                    isActive
                      ? 'bg-turbo-500 border-turbo-500 text-white'
                      : 'bg-turbo-900/40 border-turbo-500/30 text-white/80 hover:bg-turbo-900/60'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {sec.region.humanName}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-white/45 mt-2">
            Picks where the map opens and which routing DB the planner uses.
            Doesn't trigger any download.
          </p>
        </div>
      )}

      {/* Wave 14ed: Frueher stand hier eine amber Entwickler-Warnung
          ("No map files reported by the backend. Generate the PMTiles
          first ... scripts/generate-pmtiles.sh"). Seit es keine fest
          eingebaute Region mehr gibt, ist "keine Sections" der NORMALFALL
          fuer jeden neuen Account — der haette also als Erstes eine
          Fehlermeldung mit einem Shell-Skript darin gesehen. Jetzt: ein
          ruhiger Hinweis, der sagt was zu tun ist. */}
      {!busy && sections.length === 0 && (
        <div className="bg-turbo-700/30 border border-turbo-500/20 rounded-lg p-4 text-sm text-white/70 leading-relaxed">
          <div className="font-semibold text-white mb-1">Nothing downloaded</div>
          Offline maps come from a corridor you build yourself: upload a GPX
          under Navi &middot; Corridor, and once the build is done you can
          download map, contours, hillshade and POIs for it here. Until then
          the app works online only &mdash; nothing is preloaded.
        </div>
      )}

      <div className="space-y-6">
        {sections.map((section, sectionIdx) => {
          const sectionAllCached =
            section.files.length > 0 && section.files.every((f) => f.cached);
          const sectionTotal = section.files.reduce(
            (s, f) => s + f.file.sizeBytes,
            0,
          );
          const sectionCached = section.files
            .filter((f) => f.cached)
            .reduce((s, f) => s + (f.cachedSize ?? 0), 0);
          return (
            <section key={section.region.id}>
              <div className="flex items-baseline justify-between mb-2">
                <h2 className="text-lg font-semibold">
                  {section.region.humanName}
                </h2>
                <span className="text-xs text-white/55">
                  {formatBytes(sectionCached)} / {formatBytes(sectionTotal)}
                </span>
              </div>
              <div className="space-y-3">
                {section.files.map((f, fileIdx) => (
                  <OfflineCard
                    key={f.file.key}
                    state={f}
                    onDownload={() => download(sectionIdx, fileIdx)}
                    onCancel={() => cancel(sectionIdx, fileIdx)}
                    onRemove={() => remove(sectionIdx, fileIdx)}
                  />
                ))}
              </div>
              {section.files.length > 1 && !sectionAllCached && !anyDownloading && (
                armedRegion === sectionIdx ? (
                  <div className="mt-3 rounded-lg border border-amber-400/40 bg-amber-500/10 p-3">
                    <div className="text-amber-100 text-sm">
                      {formatBytes(sectionTotal - sectionCached)} für{' '}
                      {section.region.humanName} laden? Im Mobilfunk kann das
                      deinen Datentarif belasten — am besten im WLAN.
                    </div>
                    <div className="mt-2.5 flex gap-2">
                      <button
                        className="flex-1 bg-turbo-500 hover:bg-turbo-600 rounded-lg py-2 text-sm font-semibold transition"
                        onClick={() => void downloadRegion(sectionIdx)}
                      >
                        Jetzt laden
                      </button>
                      <button
                        className="flex-1 border border-white/20 text-white/70 rounded-lg py-2 text-sm font-semibold transition"
                        onClick={() => setArmedRegion(null)}
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="mt-3 w-full bg-turbo-500 hover:bg-turbo-600 rounded-lg py-2.5 text-sm font-medium transition"
                    onClick={() => setArmedRegion(sectionIdx)}
                  >
                    Download all of {section.region.humanName} ({formatBytes(sectionTotal - sectionCached)})
                  </button>
                )
              )}
              {sectionAllCached && (
                <div className="mt-3 text-center text-xs text-turbo-500 font-medium">
                  ✓ {section.region.humanName} is fully offline
                </div>
              )}
              {section.files.some((f) => f.cached) && (
                <DangerConfirm
                  label={`Remove ${section.region.humanName} from device`}
                  question={`Alle Offline-Daten für ${section.region.humanName} vom Gerät löschen? Karten, Höhenlinien und Relief dieser Region sind danach nur wieder mit Internet zu bekommen.`}
                  className="mt-2 w-full bg-red-500/15 border border-red-500/30 hover:bg-red-500/25 text-red-300 rounded-lg py-1.5 text-xs transition"
                  wrapperClassName="mt-2"
                  onConfirm={() => removeRegion(sectionIdx)}
                />
              )}
            </section>
          );
        })}

        {/* Wave 14do: Placeholder-Sections für Regionen die in REGION_IDS
            stehen aber vom Server-Manifest noch nicht angeboten werden.
            Aktuell betrifft das 'finale-outdoor' bis die PMTiles + die
            navi-finale-outdoor.sqlite gebaut sind. So weiß der User dass
            die Region geplant ist und kann später wiederkommen. */}
        {REGION_IDS.filter(
          (rid) => !sections.some((s) => s.region.id === rid),
        ).map((rid) => {
          const info = REGIONS[rid];
          if (!info) return null;
          return (
            <section
              key={`stub-${rid}`}
              className="rounded-xl border border-turbo-700/40 bg-turbo-700/15 p-4"
            >
              <div className="flex items-baseline justify-between mb-1">
                <h2 className="text-lg font-semibold text-white/70">
                  {info.humanName}
                </h2>
                <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-300/90 bg-amber-500/15 border border-amber-500/30 rounded px-1.5 py-0.5">
                  coming soon
                </span>
              </div>
              <p className="text-white/60 text-xs leading-relaxed">
                Map + routing data for this region is not available yet — the
                server-side bundles (PMTiles, contours, hillshade, navi DB)
                are still being prepared. Check back once they're live.
              </p>
            </section>
          );
        })}
      </div>

      {(anyCached || pois.some((p) => p.count > 0)) && (
        <DangerConfirm
          label="Alle Offline-Daten löschen (Karten, Höhenlinien, Relief, POIs)"
          question="Wirklich ALLES lokal Gespeicherte löschen? Karten, Höhenlinien, Relief und POI-Daten aller Regionen verschwinden von diesem Gerät. Deine Korridor-Pakete auf dem Server bleiben davon unberührt — die löschst du unter Navi · Korridor."
          confirmLabel="Ja, alles löschen"
          className="mt-6 w-full bg-red-500/20 border border-red-500/40 hover:bg-red-500/30 text-red-300 rounded-lg py-2 text-sm transition"
          wrapperClassName="mt-6"
          onConfirm={clearAll}
        />
      )}

      {/* POI-Section — visually distinct aber im selben Layout-Rhythmus wie
          die Kartendateien oben. User muss nicht mehr zwischen POI-Button
          und Offline-Tab hin- und herspringen: alles, was offline verfügbar
          sein soll, lebt hier. */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-1">Points of interest</h2>
        <p className="text-white/60 text-sm mb-3">
          Download OpenStreetMap POIs (fountains, later more) so they show on
          the map even in the forest without signal. Toggle their visibility
          from the <span aria-hidden="true">📍</span> POI button on the map.
        </p>

        <div className="space-y-3">
          {pois.map((p) => (
            <PoiCard
              key={p.category}
              state={p}
              onDownload={() => downloadPoi(p.category)}
              onRemove={() => removePoi(p.category)}
            />
          ))}
        </div>
      </div>

      <div className="mt-8 text-xs text-white/50 space-y-2">
        <p>
          <strong className="text-white/70">How it works:</strong> The maps
          are downloaded once and stored in browser storage. After that the
          app opens maps from the cache, even without internet.
        </p>
        <p>
          <strong className="text-white/70">Tip:</strong> Install the app on
          your phone as a PWA (iOS: Share → "Add to Home Screen" · Android:
          Chrome menu → "Install app"). Then it works like a native app even
          in airplane mode.
        </p>
      </div>
    </div>
  );
}

function OfflineCard({
  state,
  onDownload,
  onCancel,
  onRemove,
}: {
  state: FileState;
  onDownload: () => void;
  onCancel: () => void;
  onRemove: () => void;
}) {
  const { file, cached, progress, error } = state;
  const isDownloading = !!progress;

  // Wave 14ca-fix64: Download-Consent. User-Bug: "Zustimmung zum Download
  // der Kartendaten oder zumindest die Ankündigung der Größe waere nicht
  // verkehrt." — wir machen die Groesse prominenter (Pille direkt neben
  // dem Download-Button) UND zeigen bei groesseren Files einen Confirm-
  // Dialog mit klartext-Groesse + Hinweis auf mobile Daten.
  const sizeMB = file.sizeBytes / (1024 * 1024);
  const LARGE_THRESHOLD_MB = 25;
  const isLarge = sizeMB >= LARGE_THRESHOLD_MB;
  // Wave 14ea: statt window.confirm (in der iOS-PWA unzuverlaessig, liefert
  // still `false` -> Button tut scheinbar nichts) ein zweistufiger Tap mit
  // Bestaetigungszeile direkt in der Karte.
  const [armed, setArmed] = useState(false);
  const handleConfirmedDownload = () => {
    if (isLarge && !armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    onDownload();
  };

  return (
    <div className="bg-turbo-700/30 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{file.title}</h3>
            {cached && (
              <span className="text-[10px] uppercase tracking-wider bg-turbo-500/30 text-turbo-500 px-1.5 py-0.5 rounded">
                offline
              </span>
            )}
          </div>
          <p className="text-xs text-white/60 mt-1">{file.description}</p>
          {/* Wave 14ca-fix64: Groesse als sichtbare Pille statt grauer
              Mini-Text. Bei grossen Files (>= 25 MB) gelb-Warn-Farbe damit
              der User VOR dem Tap sieht, dass das groesser ist. */}
          <p className="mt-2">
            <span
              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md border ${
                isLarge
                  ? 'bg-amber-500/15 border-amber-400/40 text-amber-200'
                  : 'bg-turbo-900/40 border-white/10 text-white/70'
              }`}
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {formatBytes(file.sizeBytes)}
              {isLarge && ' · WLAN empfohlen'}
            </span>
          </p>
        </div>
        <div>
          {!cached && !isDownloading && (
            <button
              className="bg-turbo-500 hover:bg-turbo-600 rounded-md px-3 py-1.5 text-sm font-medium transition"
              onClick={handleConfirmedDownload}
            >
              Download
            </button>
          )}
          {cached && !isDownloading && (
            <DangerConfirm
              compact
              label="Remove"
              question=""
              className="bg-turbo-900/60 hover:bg-red-500/30 border border-turbo-700/50 hover:border-red-500/50 rounded-md px-3 py-1.5 text-sm transition"
              onConfirm={onRemove}
            />
          )}
          {isDownloading && (
            <button
              className="bg-turbo-900/60 hover:bg-red-500/30 rounded-md px-3 py-1.5 text-sm transition"
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
      {armed && !cached && !isDownloading && (
        <div className="mt-3 rounded-lg border border-amber-400/40 bg-amber-500/10 p-3">
          <div className="text-amber-100 text-sm">
            {file.title} ist {formatBytes(file.sizeBytes)} gross. Im Mobilfunk
            kann das deinen Datentarif belasten — am besten im WLAN.
          </div>
          <div className="mt-2.5 flex gap-2">
            <button
              className="flex-1 bg-turbo-500 hover:bg-turbo-600 rounded-lg py-2 text-sm font-semibold transition"
              onClick={handleConfirmedDownload}
            >
              Jetzt laden
            </button>
            <button
              className="flex-1 border border-white/20 text-white/70 rounded-lg py-2 text-sm font-semibold transition"
              onClick={() => setArmed(false)}
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {isDownloading && progress && (
        <div className="mt-3">
          <div className="h-2 bg-turbo-900/50 rounded overflow-hidden">
            <div
              className="h-full bg-turbo-500 transition-all"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-white/60 mt-1">
            <span>
              {formatBytes(progress.loaded)}
              {progress.total > 0 && ` / ${formatBytes(progress.total)}`}
            </span>
            <span>{progress.percent}%</span>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1">
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Lädt für alle registrierten POI-Kategorien den aktuellen Cache-Stand und
 * baut daraus das initiale UI-State-Array. Beim Mount einmal, nach jedem
 * Refresh wieder (damit Storage-Zahlen konsistent sind, wenn der User auf
 * "Storage frei machen" o.ä. klickt und zurück kommt).
 */
async function loadAllPoiCacheStates(): Promise<PoiCacheState[]> {
  const ids = Object.keys(POI_CATEGORIES) as PoiCategoryId[];
  const results = await Promise.all(
    ids.map(async (catId) => {
      const def = POI_CATEGORIES[catId];
      try {
        const info = await getPoiCacheInfo(catId);
        return {
          category: catId,
          label: def.label,
          emoji: def.emoji,
          description: POI_DESCRIPTIONS[catId] ?? def.hint ?? '',
          count: info.count,
          oldestCachedAt: info.oldestCachedAt,
          busy: false,
        } satisfies PoiCacheState;
      } catch {
        return {
          category: catId,
          label: def.label,
          emoji: def.emoji,
          description: POI_DESCRIPTIONS[catId] ?? def.hint ?? '',
          count: 0,
          oldestCachedAt: null,
          busy: false,
        } satisfies PoiCacheState;
      }
    }),
  );
  return results;
}

/**
 * POI-Cache-Card — visuell konsistent mit `OfflineCard`, aber mit POI-
 * spezifischem Status (Anzahl + Alter des Cache, statt Bytes). Kein
 * Progress-Bar weil Overpass-Responses ein-Shot klein sind.
 */
function PoiCard({
  state,
  onDownload,
  onRemove,
}: {
  state: PoiCacheState;
  onDownload: () => void;
  onRemove: () => void;
}) {
  const { emoji, label, description, count, oldestCachedAt, busy, error } = state;
  const hasCache = count > 0;
  const ageText = oldestCachedAt ? formatRelativeAge(oldestCachedAt) : null;

  return (
    <div className="bg-turbo-700/30 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xl" aria-hidden="true">{emoji}</span>
            <h3 className="font-medium">{label}</h3>
            {hasCache && (
              <span className="text-[10px] uppercase tracking-wider bg-turbo-500/30 text-turbo-500 px-1.5 py-0.5 rounded">
                offline
              </span>
            )}
          </div>
          <p className="text-xs text-white/60 mt-1">{description}</p>
          <p className="text-xs text-white/50 mt-1">
            {hasCache ? (
              <>
                <span className="tabular-nums">{count}</span> items cached
                {ageText && <span className="text-white/40"> · {ageText}</span>}
              </>
            ) : (
              'Not downloaded yet'
            )}
          </p>
        </div>
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          <button
            disabled={busy}
            className="bg-turbo-500 hover:bg-turbo-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-md px-3 py-1.5 text-sm font-medium transition"
            onClick={onDownload}
          >
            {busy ? (
              <span className="flex items-center gap-1.5">
                <span className="animate-spin inline-block">↻</span>
                <span>Downloading…</span>
              </span>
            ) : hasCache ? (
              'Refresh'
            ) : (
              'Download'
            )}
          </button>
          {hasCache && !busy && (
            <DangerConfirm
              compact
              label="Remove"
              question=""
              className="bg-turbo-900/60 hover:bg-red-500/30 border border-turbo-700/50 hover:border-red-500/50 rounded-md px-3 py-1.5 text-sm transition"
              onConfirm={onRemove}
            />
          )}
        </div>
      </div>

      {error && (
        <div className="mt-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1 leading-snug">
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Service-Worker-Status-Card — wird ganz oben auf der OfflinePage angezeigt.
 *
 * Wave 14aw: Ohne aktiven SW werden PMTiles-Range-Requests nicht abgefangen
 * und die Karte zeigt offline nur leeren Hintergrund — selbst wenn der Cache
 * physisch gefüllt ist. Dieses stille Failure-Szenario ist schwer zu
 * diagnostizieren wenn man es nicht sieht. Die Card macht es sichtbar:
 *   - active  → kleiner grüner Hinweis (beruhigend, keine Aktion nötig)
 *   - unknown → dezent grau (meist im Dev-Modus oder während Start)
 *   - failed  → lauter roter Hinweis mit Fix-Anleitung
 *   - unsupported → gelber Hinweis (Safari Private Mode o.ä.)
 *
 * Im Dev-Modus zeigen wir 'unknown' gar nicht — dort ist der SW absichtlich
 * deaktiviert und die Warnung wäre nur Lärm.
 */
function ServiceWorkerStatusCard() {
  const state = useServiceWorkerState();
  if (state === 'unknown' && import.meta.env.DEV) return null;
  return <SwStatusRow state={state} />;
}

function SwStatusRow({ state }: { state: ServiceWorkerState }) {
  if (state === 'active') {
    return (
      <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-lg px-3 py-2 text-xs mb-4 flex items-center gap-2">
        <span aria-hidden="true">✓</span>
        <span>
          <strong className="text-emerald-200">Offline engine:</strong> active.
          Your cached maps will work without a signal.
        </span>
      </div>
    );
  }
  if (state === 'unknown') {
    return (
      <div className="bg-turbo-700/30 border border-turbo-700/50 text-white/70 rounded-lg px-3 py-2 text-xs mb-4 flex items-center gap-2">
        <span className="animate-spin inline-block" aria-hidden="true">↻</span>
        <span>
          <strong className="text-white/85">Offline engine:</strong> starting…
        </span>
      </div>
    );
  }
  if (state === 'unsupported') {
    return (
      <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-lg px-3 py-2 text-xs mb-4">
        <div>
          <strong className="text-amber-100">Offline engine:</strong> not
          supported in this browser.
        </div>
        <div className="mt-1 text-amber-200/80">
          This looks like Safari Private Mode. Open the app in a normal window
          or install it to the home screen to enable offline maps.
        </div>
      </div>
    );
  }
  // 'failed'
  return (
    <div className="bg-red-500/10 border border-red-500/40 text-red-200 rounded-lg px-3 py-2 text-xs mb-4">
      <div>
        <strong className="text-red-100">Offline engine:</strong> not active.
      </div>
      <div className="mt-1 text-red-200/85">
        Offline caching is not running right now. Please close and reopen the
        app — once it's back up, offline maps will work.
      </div>
    </div>
  );
}

/**
 * Relative-age-Formatter für POI-Cache ("2d ago"). Duplikat-frei aus dem
 * POIButton gezogen als der Button seinen Status verloren hat.
 */
function formatRelativeAge(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

/**
 * Zwei-Schritt-Bestaetigung OHNE window.confirm.
 *
 * Warum eigene Komponente: in der iOS-Homescreen-PWA (display: standalone)
 * unterdrueckt WebKit window.confirm/alert/prompt. confirm() liefert dort
 * sofort `false` — der Loesch-Handler steigt still aus, es gibt keinen
 * Request, keine Fehlermeldung, kein Dialog. Aus User-Sicht: "ich klicke
 * und es passiert NICHTS." Dieselbe Falle hat schon beim Loeschen von
 * Korridor-Paketen zugeschlagen (Wave 14dz) — hier ist der Rest davon.
 *
 * `compact` fuer schmale Spalten (Karten-Buttons): dort passt keine
 * Textzeile, also zwei kleine Buttons untereinander.
 */
function DangerConfirm({
  label,
  question,
  confirmLabel = 'Ja, löschen',
  className,
  wrapperClassName,
  compact = false,
  onConfirm,
}: {
  label: string;
  question: string;
  confirmLabel?: string;
  className?: string;
  wrapperClassName?: string;
  compact?: boolean;
  onConfirm: () => void | Promise<void>;
}) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      setArmed(false);
    } catch (err) {
      // Fehler sichtbar machen statt still schlucken — "es passiert nichts"
      // darf es in dieser App nicht mehr geben.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!armed) {
    return (
      <button type="button" className={className} onClick={() => setArmed(true)}>
        {label}
      </button>
    );
  }

  if (compact) {
    return (
      <div className="flex flex-col gap-1.5">
        <button
          type="button"
          disabled={busy}
          onClick={() => void run()}
          className="bg-red-500/80 hover:bg-red-500 rounded-md px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40 transition"
        >
          {busy ? '…' : 'Wirklich'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setArmed(false)}
          className="border border-white/20 text-white/70 rounded-md px-3 py-1.5 text-sm disabled:opacity-40 transition"
        >
          Abbrechen
        </button>
        {error && (
          <div className="text-[11px] text-red-300 leading-snug">{error}</div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`${wrapperClassName ?? ''} rounded-lg border border-red-500/40 bg-red-500/10 p-3`}
    >
      <div className="text-red-100 text-sm leading-snug">{question}</div>
      <div className="mt-2.5 flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => void run()}
          className="flex-1 rounded-lg bg-red-500/80 hover:bg-red-500 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40 transition"
        >
          {busy ? 'Lösche…' : confirmLabel}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setArmed(false)}
          className="flex-1 rounded-lg border border-white/20 px-3 py-2 text-sm font-semibold text-white/70 disabled:opacity-40 transition"
        >
          Abbrechen
        </button>
      </div>
      {error && (
        <div className="mt-2 text-xs text-red-300 leading-snug">{error}</div>
      )}
    </div>
  );
}
